/* eslint-disable max-len */
import React, {
  useEffect, useState, useRef, useContext,
} from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import {
  Box, Paper, Divider, Chip, Button,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Table from '../../components/Table';
import PieChart from '../../components/PieChart';
import BarChart from '../../components/BarChart';
import APIWrapper from '../../utils/graphqlwrapper';
import AppContext from '../../context';
import logger from '../../utils/logger';
import subscriptionService from '../../services/SubscriptionService';
import { sumBySourceMonthlyResultsColumns } from '../../utils/schemaInfo';
import { createSumBySourceOverviewResultsRows } from '../../utils/tableHelpers';

dayjs.extend(utc);
dayjs.extend(timezone);
const initColumnVisibilityModel = {
  channel_category: true,
  gift_count: true,
  amount: true,
  percent_of_total: true,
};

const defaultSortModel = [{ field: 'amount', sort: 'desc' }];

// Summary stats (Raised Today / Raised this Month) always reflect data without exclusions.
// Only update them when the run config matches these defaults.
const DEFAULT_RUN_CONFIG = JSON.stringify({
  includeExclusions: false,
  separateRecurring: true,
  removeTotals: false,
});

function SBSOverview({ onData }) {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    initColumnVisibilityModel,
  );
  const [pieChartData, setPieChartData] = useState([]);
  const [barChartData, setBarChartData] = useState({ loading: true });
  const [pageState, setPageState] = useState({
    isLoading: true,
    page: 0, // mui pages are 0 indexed
    pageSize: 10,
    data: [],
    total: 0,
  });
  const hasFetchedSBSData = useRef(false);
  const committedRunConfig = useRef(null);
  const { setShouldLogOut, authenticated } = useContext(AppContext);
  const [sortModel, setSortModel] = useState(defaultSortModel);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [currentExecutionId, setCurrentExecutionId] = useState(null);
  const { client } = useParams();

  const [formData, setFormData] = useState({
    removeTotals: false,
    includeExclusions: false,
    separateRecurring: true,
  });

  const {
    includeExclusions, removeTotals, separateRecurring,
  } = formData;
  const columns = sumBySourceMonthlyResultsColumns;
  const getDefaultColumns = () => columns;

  const hasPendingChanges = JSON.stringify({
    includeExclusions: formData.includeExclusions,
    separateRecurring: formData.separateRecurring,
    removeTotals: formData.removeTotals,
  }) !== committedRunConfig.current;

  const handleUpdateTable = async () => {
    const newRunConfig = JSON.stringify({
      includeExclusions: formData.includeExclusions,
      separateRecurring: formData.separateRecurring,
      removeTotals: formData.removeTotals,
    });

    if (newRunConfig === committedRunConfig.current) return;

    try {
      setPageState((old) => ({ ...old, isLoading: true }));
      const stepFunctionResult = await APIWrapper.queryApi(
        { query: 'sumBySourceStepFunction' },
        setShouldLogOut,
        {
          clientID: client,
          startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
          endDate: dayjs().format('YYYY-MM-DD'),
          includeExclusions: formData.includeExclusions,
          separateRecurring: formData.separateRecurring,
          removeTotals: formData.removeTotals,
          aggType: 'sumByChannelCategory',
          breakdownPeriod: 'day',
          downloadRequest: false,
        },
      );
      const executionId = JSON.parse(stepFunctionResult.data[0]).id;
      committedRunConfig.current = newRunConfig;
      setCurrentExecutionId(executionId);
      await subscriptionService.startSumBySourceDisplaySubscription(client);
    } catch (error) {
      logger.error('Error updating SBS display data:', error);
      setPageState((old) => ({ ...old, isLoading: false }));
    }
  };

  const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

  const applyDisplayData = (displayDataJson, runConfig) => {
    try {
      const parsedData = JSON.parse(displayDataJson);
      const { formedRows, pinnedRows } = createSumBySourceOverviewResultsRows(parsedData, runConfig);
      setPageState((old) => ({
        ...old,
        isLoading: false,
        data: formedRows,
        total: formedRows.length,
        totalRow: pinnedRows,
      }));
      // set barChart data based on table data
      setBarChartData({ ...parsedData, loading: false });
    } catch (err) {
      logger.error('Error parsing SBS display data:', err);
      setPageState((old) => ({ ...old, isLoading: false }));
    }
  };

  // Listen for display subscription updates
  useEffect(() => {
    const handleDisplayUpdate = (e) => {
      const { status, data } = e.detail;
      if (status === 'Success' && data.display_data) {
        const parsedRunConfig = JSON.parse(committedRunConfig.current);
        applyDisplayData(data.display_data, parsedRunConfig);
        if (committedRunConfig.current === DEFAULT_RUN_CONFIG) {
          const formattedDateTime = dayjs().tz('America/New_York').format('MM/DD/YYYY hh:mma z');
          onData(data.display_data, formattedDateTime);
        }
      } else if (status === 'Failed') {
        logger.error('SBS display step function failed');
        setPageState((old) => ({ ...old, isLoading: false }));
      }
    };

    window.addEventListener('sbsDisplayUpdate', handleDisplayUpdate);
    return () => window.removeEventListener('sbsDisplayUpdate', handleDisplayUpdate);
  }, [currentExecutionId]);

  // On load: check cache, use fresh data or trigger step function
  useEffect(() => {
    if (!authenticated || !client || hasFetchedSBSData.current) return;
    hasFetchedSBSData.current = true;

    const loadData = async () => {
      try {
        const runConfigObject = {
          includeExclusions: formData.includeExclusions,
          separateRecurring: formData.separateRecurring,
          removeTotals: formData.removeTotals,
        };
        const currentRunConfig = JSON.stringify(runConfigObject);
        logger.info('Loading SBS display data with run config:', currentRunConfig);

        // Check for a cached entry
        const cacheResult = await APIWrapper.queryApi(
          { query: 'sumBySourceDisplayCacheByClientID' },
          setShouldLogOut,
          { client_id: client },
        );
        const cacheItem = cacheResult && cacheResult.data && cacheResult.data[0] && cacheResult.data[0][0];

        // Cache hit: Success within TTL with matching run config
        const isFresh = cacheItem
          && cacheItem.status === 'Success'
          && cacheItem.display_data
          && Date.now() - new Date(cacheItem.updatedAt).getTime() < CACHE_TTL_MS
          && cacheItem.run_config === currentRunConfig;

        if (isFresh) {
          logger.info('SBS display cache hit, using cached data');
          applyDisplayData(cacheItem.display_data, runConfigObject);
          committedRunConfig.current = currentRunConfig;
          if (currentRunConfig === DEFAULT_RUN_CONFIG) {
            const formattedDateTime = dayjs(cacheItem.updatedAt).tz('America/New_York').format('MM/DD/YYYY hh:mma z');
            onData(cacheItem.display_data, formattedDateTime);
          }
          return;
        }

        // Step function already in flight: Pending row created within last 10 minutes
        const PENDING_TTL_MS = 10 * 60 * 1000;
        const isAlreadyRunning = cacheItem
          && cacheItem.status === 'Pending'
          && cacheItem.createdAt
          && Date.now() - new Date(cacheItem.createdAt).getTime() < PENDING_TTL_MS;

        if (isAlreadyRunning) {
          logger.info('SBS step function already in flight, subscribing to existing run');
          committedRunConfig.current = cacheItem.run_config ? cacheItem.run_config : currentRunConfig;
          await subscriptionService.startSumBySourceDisplaySubscription(client);
          return;
        }

        // No fresh cache — trigger the async step function and subscribe
        logger.info('SBS display cache miss, triggering step function');
        const stepFunctionResult = await APIWrapper.queryApi(
          { query: 'sumBySourceStepFunction' },
          setShouldLogOut,
          {
            clientID: client,
            startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            includeExclusions: formData.includeExclusions,
            separateRecurring: formData.separateRecurring,
            removeTotals: formData.removeTotals,
            aggType: 'sumByChannelCategory',
            breakdownPeriod: 'day',
            downloadRequest: false,
          },
        );
        const executionId = JSON.parse(stepFunctionResult.data[0]).id;
        committedRunConfig.current = currentRunConfig;
        setCurrentExecutionId(executionId);
        await subscriptionService.startSumBySourceDisplaySubscription(client);
      } catch (error) {
        logger.error('Error loading SBS display data:', error);
        setPageState((old) => ({ ...old, isLoading: false }));
      }
    };

    loadData();
  }, [authenticated, client]);

  useEffect(() => {
    // set and format pieChart data based on table data
    const pieData = pageState.data.map((row, index) => ({
      id: index,
      value: row.percent_of_total,
      label: row.channel_category,
    }));
    // remove the total row from the pie chart data
    // the total row is just for the table
    const filteredPieData = pieData.filter((item) => item.label !== 'Total');
    setPieChartData(filteredPieData);
  }, [pageState.data]);

  return (
    <Box sx={{
      width: '100%',
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      gap: 2,
    }}
    >
      <Paper
        variant="outlined"
        sx={{
          margin: '10px', padding: '20px', flex: '1 1 55%', minWidth: '400px',
        }}
      >
        <Box sx={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2,
        }}
        >
          <Chip
            label="Remove Totals"
            onClick={() => setFormData({ ...formData, removeTotals: !removeTotals })}
            color={removeTotals ? 'primary' : 'default'}
            variant={removeTotals ? 'filled' : 'outlined'}
            icon={removeTotals ? <DoneIcon /> : null}
          />
          <Chip
            label="Include Exclusions"
            onClick={() => setFormData({ ...formData, includeExclusions: !includeExclusions })}
            color={includeExclusions ? 'primary' : 'default'}
            variant={includeExclusions ? 'filled' : 'outlined'}
            icon={includeExclusions ? <DoneIcon /> : null}
          />
          <Chip
            label="Separate Recurring"
            onClick={() => setFormData({ ...formData, separateRecurring: !separateRecurring })}
            color={separateRecurring ? 'primary' : 'default'}
            variant={separateRecurring ? 'filled' : 'outlined'}
            icon={separateRecurring ? <DoneIcon /> : null}
          />
          <Button
            sx={{ marginLeft: 'auto' }}
            variant="contained"
            onClick={handleUpdateTable}
            disabled={!hasPendingChanges || pageState.isLoading}
          >
            Apply Filters
          </Button>
        </Box>
        <Box>
          <Table
            pageState={pageState}
            updatePageState={setPageState}
            columns={columns}
            sortModel={sortModel}
            setSortModel={setSortModel}
            columnVisibilityModel={columnVisibilityModel}
            setColumnVisibilityModel={setColumnVisibilityModel}
            filterModel={filterModel}
            setFilterModel={setFilterModel}
            tablePrefix="sbsmonthlyresults"
            getDefaultColumns={getDefaultColumns}
          />
        </Box>
      </Paper>
      <Paper
        variant="outlined"
        sx={{
          margin: '10px', padding: '20px', flex: '1 1 30%', minWidth: '250px', display: 'flex', flexDirection: 'column', gap: 2,
        }}
      >
        <Box>
          <p className="chart-header">Revenue Breakdown</p>
          <PieChart data={pieChartData} loading={pageState.isLoading} />
        </Box>
        <Divider sx={{ marginBottom: '30px', marginTop: '30px' }} />
        <Box>
          <p className="chart-header">Revenue By Day</p>
          <BarChart data={barChartData} />
        </Box>
      </Paper>
    </Box>
  );
}

export default SBSOverview;

SBSOverview.propTypes = {
  onData: PropTypes.func.isRequired,
};
